import { TableColumn, TableSpec } from './copilot.types';

export type ExtractedAnswerTables = {
  /** Answer with tabular blocks removed (prose kept). */
  content: string;
  tables: TableSpec[];
};

/**
 * When the gateway did not attach structured tables, pull GFM / TSV-style
 * grids out of the assistant markdown so Copilot can render them with the
 * table presenter. API stays unchanged — this is display-only.
 */
export function extractInlineTablesFromAnswer(content: string): ExtractedAnswerTables {
  const text = (content ?? '').trim();
  if (!text) {
    return { content: text, tables: [] };
  }

  const tables: TableSpec[] = [];
  let remaining = text;
  let index = 0;

  // Prefer GFM pipe tables, then tab/space-aligned grids.
  const gfm = extractGfmTables(remaining, index);
  remaining = gfm.content;
  tables.push(...gfm.tables);
  index += gfm.tables.length;

  const aligned = extractAlignedTables(remaining, index);
  remaining = aligned.content;
  tables.push(...aligned.tables);

  return {
    content: remaining.replace(/\n{3,}/g, '\n\n').trim(),
    tables,
  };
}

function extractGfmTables(content: string, startIndex: number): ExtractedAnswerTables {
  const tables: TableSpec[] = [];
  // Header | --- | Data rows (at least one). Allows optional leading/trailing pipes.
  const re =
    /(?:^|\n)((?:[^\n]*\|[^\n]*\n)(?:\s*\|?(?:\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*\n)(?:[^\n]*\|[^\n]*(?:\n|$))+)/g;

  let out = content;
  const matches = [...content.matchAll(re)];
  // Replace from the end so earlier indices stay valid.
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i];
    const block = m[1];
    const parsed = parseGfmBlock(block);
    if (!parsed) {
      continue;
    }
    const title = precedingHeading(content, m.index ?? 0) || parsed.title;
    tables.unshift({
      id: `inline_table_${startIndex + i}`,
      title,
      columns: parsed.columns,
      rows: parsed.rows,
      sourceTool: 'inline_answer',
    });
    const start = m.index ?? 0;
    const full = m[0];
    // Keep the leading newline when the match included one.
    const lead = full.startsWith('\n') ? '\n' : '';
    out = out.slice(0, start) + lead + out.slice(start + full.length);
  }
  return { content: out, tables };
}

function parseGfmBlock(
  block: string,
): { title: string, columns: TableColumn[], rows: Record<string, unknown>[] } | null {
  const lines = block
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 3) {
    return null;
  }
  const headerCells = splitPipeRow(lines[0]);
  if (headerCells.length < 2 || !isSeparatorRow(lines[1])) {
    return null;
  }
  const dataLines = lines.slice(2).filter((line) => !isSeparatorRow(line) && !looksLikeTotalLine(line));
  let rowCells = dataLines
    .map((line) => splitPipeRow(line))
    .filter((cells) => cells.length >= 2);
  if (rowCells.length < 1) {
    return null;
  }
  // Models sometimes emit a pipe table with no real header — just the first
  // row of data (e.g. a bullet-marker column plus the first admin's name).
  // Never promote that into column labels; fall back to generic ones.
  const hasRealHeader = looksLikeHeaderRow(headerCells);
  const labels = hasRealHeader ? headerCells : headerCells.map(() => '');
  if (!hasRealHeader) {
    rowCells = [headerCells, ...rowCells];
  }
  const columns = labels.map((label, i) =>
    columnFor(
      label,
      i,
      rowCells.map((cells) => cells[i] ?? ''),
    ),
  );
  const rows = rowCells.map((cells) => {
    const row: Record<string, unknown> = {};
    columns.forEach((col, i) => {
      row[col.key] = stripInlineMarkdownEmphasis(cells[i] ?? '');
    });
    return row;
  });
  return {
    title: hasRealHeader ? stripInlineMarkdownEmphasis(headerCells.join(' / ')) : 'Table',
    columns,
    rows,
  };
}

function extractAlignedTables(content: string, startIndex: number): ExtractedAnswerTables {
  const lines = content.split('\n');
  const tables: TableSpec[] = [];
  const keep: string[] = [];
  let i = 0;
  let tableSeq = startIndex;

  while (i < lines.length) {
    const firstCells = splitAlignedRow(lines[i]);
    const hasRealHeader =
      firstCells.length >= 2 && looksLikeHeaderRow(firstCells);
    // Models often emit name/duration grids with no header row. Treat those as
    // data and invent Person / Hours columns — never promote the first member
    // into the header (that put "Jordi C:" where "Person" belongs).
    const firstIsPersonDuration =
      firstCells.length >= 2 && looksLikePersonDurationRow(firstCells);

    // A row that is neither a real header nor a person/duration row can still
    // be a repeating grid shape (e.g. a marker column + name column with no
    // label row) — show it as a table with generic column labels below,
    // instead of dropping it back to prose.
    if (firstCells.length < 2) {
      keep.push(lines[i]);
      i += 1;
      continue;
    }

    const headerCells = hasRealHeader
      ? firstCells
      : firstIsPersonDuration
        ? ['Person', 'Hours']
        : firstCells.map(() => '');
    const dataRows: string[][] = [];
    let j = hasRealHeader ? i + 1 : i;
    while (j < lines.length) {
      const trimmed = lines[j].trim();
      if (!trimmed) {
        break;
      }
      if (looksLikeTotalLine(trimmed)) {
        break;
      }
      const cells = splitAlignedRow(lines[j]);
      if (hasRealHeader) {
        if (cells.length !== headerCells.length) {
          break;
        }
        dataRows.push(cells);
      } else if (firstIsPersonDuration) {
        if (!looksLikePersonDurationRow(cells)) {
          break;
        }
        // Normalize list markers ("- Jordi C:") and trailing name colons.
        dataRows.push([
          stripListMarker(cells[0]).replace(/:$/, '').trim(),
          cells[cells.length - 1],
        ]);
      } else {
        // Generic headerless grid: require the same shape as the first row
        // and bail on a different kind of data row (mixed content, not a
        // uniform grid). Don't bail on looksLikeHeaderRow here — a data
        // value that happens to contain a header keyword (e.g. a person
        // named "React Test User" matches \buser\b) would otherwise split
        // the grid and get swallowed as a bogus header for the remainder.
        if (
          cells.length !== firstCells.length ||
          looksLikePersonDurationRow(cells)
        ) {
          break;
        }
        dataRows.push(cells);
      }
      j += 1;
    }

    if (dataRows.length >= 2) {
      const columns = headerCells.map((label, colIdx) =>
        columnFor(
          label,
          colIdx,
          dataRows.map((r) => r[colIdx] ?? ''),
        ),
      );
      const rows = dataRows.map((cells) => {
        const row: Record<string, unknown> = {};
        columns.forEach((col, colIdx) => {
          row[col.key] = stripInlineMarkdownEmphasis(cells[colIdx] ?? '');
        });
        return row;
      });
      const title =
        precedingHeadingFromLines(lines, i) ||
        (hasRealHeader
          ? stripInlineMarkdownEmphasis(headerCells.join(' / '))
          : firstIsPersonDuration
            ? 'Team tracked time'
            : 'Table');
      tables.push({
        id: `inline_table_${tableSeq++}`,
        title,
        columns,
        rows,
        sourceTool: 'inline_answer',
      });
      // Drop the table lines from prose; keep a blank line as separator.
      if (keep.length && keep[keep.length - 1].trim() !== '') {
        keep.push('');
      }
      i = j;
      continue;
    }

    keep.push(lines[i]);
    i += 1;
  }

  return { content: keep.join('\n'), tables };
}

function splitPipeRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => c.trim());
}

function isSeparatorRow(line: string): boolean {
  const cells = splitPipeRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

/** Split on tabs or 2+ spaces (common model formatting for ad-hoc grids). */
function splitAlignedRow(line: string): string[] {
  // Strip the list marker first — otherwise "* Name: 1h 2m" splits on the
  // marker's own trailing whitespace into a bogus ["*", "Name: 1h 2m"] pair
  // instead of being read as one bullet item.
  const trimmed = stripListMarker(line);
  if (!trimmed) {
    return [];
  }
  if (trimmed.includes('\t')) {
    return trimmed.split('\t').map((c) => c.trim()).filter((c) => c.length > 0);
  }
  // Two-or-more spaces, or a mid-line run of spaces before a duration/time token.
  const parts = trimmed.split(/\s{2,}/).map((c) => c.trim()).filter((c) => c.length > 0);
  if (parts.length >= 2) {
    return parts;
  }
  // Fallback: "Name 0h 2m 21s" — last token cluster is duration.
  const durationMatch = trimmed.match(/^(.*?)(\d+h(?:\s*\d+m)?(?:\s*\d+s)?|\d+m(?:\s*\d+s)?|\d+s)$/i);
  if (durationMatch) {
    const left = durationMatch[1].trim();
    const right = durationMatch[2].trim();
    if (left && right) {
      return [left, right];
    }
  }
  return [trimmed];
}

function looksLikeHeaderRow(cells: string[]): boolean {
  // Never promote a data row (name + duration) into column headers.
  if (looksLikePersonDurationRow(cells)) {
    return false;
  }
  // A bare bullet/marker cell ("*", "-", "•") means this is a data row, not a label row.
  if (cells.some((c) => /^[*\-•]$/.test(c.trim()))) {
    return false;
  }
  const joined = cells.join(' ').toLowerCase();
  if (/\b(name|user|person|member|employee|team|time|hours|idle|unproductive|productive|activity|total|date)\b/.test(joined)) {
    return true;
  }
  // Short generic labels only — no digits, no trailing "Name:" data markers.
  return (
    cells.length >= 2 &&
    cells.every(
      (c) =>
        c.length <= 40 &&
        !/[.!?]$/.test(c) &&
        !/\d/.test(c) &&
        !/:$/.test(c.trim()),
    )
  );
}

/** "Jordi C:" / "- Zaigham Rana" + "8h 44m" — a person metrics data row. */
function looksLikePersonDurationRow(cells: string[]): boolean {
  if (cells.length < 2) {
    return false;
  }
  const name = stripListMarker(cells[0]);
  const value = cells[cells.length - 1];
  if (!looksLikeDuration(value)) {
    return false;
  }
  // Person labels: letters/spaces, optional trailing colon; not header words alone.
  const bare = name.replace(/:$/, '').trim();
  if (!bare || bare.length > 60) {
    return false;
  }
  if (/^(name|user|person|member|employee|hours?|time|total)$/i.test(bare)) {
    return false;
  }
  // Chronological worklog lines ("04:59 AM - 05:10 AM: Paid Leave") are not
  // person/hours grids — extracting them yields a crushed 2-column table.
  if (/\b\d{1,2}:\d{2}\s*(AM|PM)\b/i.test(bare)) {
    return false;
  }
  if (/\b(paid leave|computer activity|unpaid leave|meeting)\b/i.test(bare)) {
    return false;
  }
  return /[A-Za-z]/.test(bare) && !looksLikeDuration(bare);
}

function stripListMarker(value: string): string {
  return value.trim().replace(/^[-*•]\s+/, '');
}

function looksLikeTotalLine(line: string): boolean {
  return /^\s*(\*\*)?total\b/i.test(line.trim());
}

function columnFor(label: string, index: number, sampleValues: string[]): TableColumn {
  const key = `col_${index}`;
  const samples = sampleValues.filter((v) => v.trim().length > 0);
  // Keep pre-formatted durations ("0h 2m 21s") as text — the grid's duration
  // type expects raw seconds + `<key>Formatted`.
  const allNumber =
    samples.length > 0 &&
    samples.every((v) => /^-?\d+(\.\d+)?%?$/.test(v.trim())) &&
    !samples.some((v) => looksLikeDuration(v));
  let type: TableColumn['type'] = 'text';
  if (allNumber) {
    type = samples.some((v) => v.includes('%')) ? 'percent' : 'number';
  }
  return {
    key,
    label: stripInlineMarkdownEmphasis(label) || `Column ${index + 1}`,
    type,
    sortable: true,
  };
}

/**
 * Copilot table grid renders plain text (no markdown). Strip emphasis markers
 * so cells like `**Unproductive Time**` show as "Unproductive Time".
 */
export function stripInlineMarkdownEmphasis(value: string): string {
  return value
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\*\*/g, '')
    .trim();
}

function looksLikeDuration(value: string): boolean {
  return /^\d+h(?:\s*\d+m)?(?:\s*\d+s)?$|^\d+m(?:\s*\d+s)?$|^\d+s$/i.test(value.trim());
}

function precedingHeading(content: string, tableIndex: number): string | null {
  const before = content.slice(0, tableIndex);
  const lines = before.split('\n').map((l) => l.trim()).filter(Boolean);
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const md = line.match(/^#{1,3}\s+(.+)$/);
    if (md) {
      return md[1].trim();
    }
    // Bold single-line title: **Unproductive Time — Team**
    const bold = line.match(/^\*\*(.+)\*\*$/);
    if (bold && bold[1].length <= 80) {
      return bold[1].trim();
    }
  }
  return null;
}

function precedingHeadingFromLines(lines: string[], tableLineIndex: number): string | null {
  for (let i = tableLineIndex - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line) {
      continue;
    }
    const md = line.match(/^#{1,3}\s+(.+)$/);
    if (md) {
      return md[1].trim();
    }
    const bold = line.match(/^\*\*(.+)\*\*$/);
    if (bold && bold[1].length <= 80) {
      return bold[1].trim();
    }
    // Stop at prior paragraph prose.
    if (line.length > 80 || /[.!?]$/.test(line)) {
      break;
    }
  }
  return null;
}
